import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, UserPlus, DollarSign, Wrench, Landmark, Receipt, ArrowUpRight, Radio } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const ICONS = {
  "user-plus": UserPlus,
  "dollar-sign": DollarSign,
  "wrench": Wrench,
  "landmark": Landmark,
  "receipt": Receipt,
};

const TONES = {
  tenant:       { chip: "bg-indigo-50 text-indigo-700",   ring: "ring-indigo-100" },
  rent_payment: { chip: "bg-emerald-50 text-emerald-700", ring: "ring-emerald-100" },
  work_order:   { chip: "bg-amber-50 text-amber-700",     ring: "ring-amber-100" },
  loan_payment: { chip: "bg-slate-100 text-slate-700",    ring: "ring-slate-200" },
  expense:      { chip: "bg-rose-50 text-rose-700",       ring: "ring-rose-100" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  const wks = Math.floor(days / 7);
  if (wks < 5) return `${wks}w ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Investor "Recent activity" live feed.
 *
 * Connects to `/api/activity/stream` via Server-Sent Events. The server
 * emits a `snapshot` on connect + `new` events as activity occurs
 * (detected via server-side polling every 3s). New rows fade in and pulse
 * for ~3s; a "LIVE" chip replaces the "last N" label while the EventSource
 * is connected. An "N new since page load" counter appears in the header
 * whenever fresh activity arrives — it auto-resets when the user clicks it.
 *
 * Falls back to a one-shot `/api/activity/recent` fetch if EventSource
 * isn't available or the stream dies (e.g., proxy timeout).
 */
export default function RecentActivityWidget({ limit = 10 }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [connected, setConnected] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [pulseIds, setPulseIds] = useState(new Set());
  const esRef = useRef(null);
  const pulseTimeoutsRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;

    // Try SSE first — falls back to one-shot fetch on error.
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("rv_token") : null;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    const fallbackFetch = () => {
      api.get(`/activity/recent?limit=${limit}`)
        .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
        .catch((e) => { if (!cancelled) setErr(formatApiError(e.response?.data?.detail) || "Failed to load activity"); });
    };

    if (!token || typeof EventSource === "undefined" || !base) {
      fallbackFetch();
      return () => { cancelled = true; };
    }

    const url = `${base}/api/activity/stream?token=${encodeURIComponent(token)}&limit=${limit}&poll_seconds=3`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("snapshot", (evt) => {
      if (cancelled) return;
      try {
        const payload = JSON.parse(evt.data);
        setItems(payload.items || []);
        setErr(null);
        setConnected(true);
      } catch {
        // malformed — ignore
      }
    });

    es.addEventListener("new", (evt) => {
      if (cancelled) return;
      try {
        const ev = JSON.parse(evt.data);
        setItems((prev) => {
          const existing = prev || [];
          // Skip if we've already seen this id (e.g. reconnection race)
          if (existing.some((x) => x.id === ev.id)) return existing;
          return [ev, ...existing].slice(0, limit);
        });
        setNewCount((n) => n + 1);
        setPulseIds((prev) => {
          const next = new Set(prev);
          next.add(ev.id);
          return next;
        });
        // Clear pulse after 3s so the animation ends
        const existingTimeout = pulseTimeoutsRef.current.get(ev.id);
        if (existingTimeout) clearTimeout(existingTimeout);
        const to = setTimeout(() => {
          if (cancelled) return;
          setPulseIds((prev) => {
            const next = new Set(prev);
            next.delete(ev.id);
            return next;
          });
          pulseTimeoutsRef.current.delete(ev.id);
        }, 3000);
        pulseTimeoutsRef.current.set(ev.id, to);
      } catch {
        // malformed — ignore
      }
    });

    es.onerror = () => {
      // EventSource auto-reconnects, but mark UI as disconnected in the
      // meantime. If the connection never opens we fallback.
      setConnected(false);
      if (es.readyState === EventSource.CLOSED) {
        // permanent failure → fall back
        es.close();
        if (items === null) fallbackFetch();
      }
    };

    return () => {
      cancelled = true;
      es.close();
      // Snapshot the ref value now so the cleanup doesn't race with a later
      // re-render that swaps pulseTimeoutsRef.current to a different Map.
      const timers = pulseTimeoutsRef.current;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);  // `items` intentionally excluded to avoid reconnecting the SSE on every event

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="recent-activity-widget">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-500" />
          <div>
            <div className="overline text-slate-500">{connected ? "Live" : "Feed"}</div>
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Recent activity</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200"
              data-testid="recent-activity-live-chip"
              title="Receiving events in real time"
            >
              <Radio className="w-2.5 h-2.5 animate-pulse" />
              Live
            </span>
          )}
          {newCount > 0 && (
            <button
              type="button"
              onClick={() => setNewCount(0)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-widest bg-slate-900 text-white hover:bg-slate-800 transition-colors animate-pulse"
              data-testid="recent-activity-new-count"
              title="Click to dismiss"
            >
              {newCount} new
            </button>
          )}
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">last {limit}</span>
        </div>
      </div>

      {err && (
        <div className="p-5 text-sm text-red-600" data-testid="recent-activity-error">{err}</div>
      )}

      {!err && items === null && (
        <ul className="divide-y divide-slate-100" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className="p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-slate-100 animate-pulse shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-3/4 bg-slate-100 rounded-sm animate-pulse" />
                <div className="h-2 w-1/2 bg-slate-100 rounded-sm animate-pulse" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {!err && items && items.length === 0 && (
        <div className="p-10 text-center" data-testid="recent-activity-empty">
          <Activity className="w-6 h-6 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600 font-semibold">Nothing yet</div>
          <div className="text-xs text-slate-400 mt-1">Add a property, tenant, or work order to see activity here.</div>
        </div>
      )}

      {!err && items && items.length > 0 && (
        <ul className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto" data-testid="recent-activity-list">
          {items.map((ev, i) => {
            const Icon = ICONS[ev.icon] || Activity;
            const tone = TONES[ev.type] || TONES.tenant;
            const isPulsing = pulseIds.has(ev.id);
            return (
              <li
                key={ev.id || `${ev.type}-${i}-${ev.timestamp}`}
                data-testid={`recent-activity-row-${ev.type}-${i}`}
                className={isPulsing ? "activity-row-pulse" : ""}
              >
                <Link
                  to={ev.link}
                  className="flex items-start gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group"
                >
                  <div className={`w-8 h-8 rounded-sm ring-1 ${tone.ring} ${tone.chip} flex items-center justify-center shrink-0 ${isPulsing ? "animate-pulse" : ""}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 font-medium truncate" title={ev.title}>
                      {ev.title}
                    </div>
                    {ev.subtitle && (
                      <div className="text-xs text-slate-500 truncate font-mono mt-0.5">{ev.subtitle}</div>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono whitespace-nowrap shrink-0 pt-0.5 flex items-center gap-1 group-hover:text-slate-700 transition-colors">
                    {timeAgo(ev.timestamp)}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
